import React, { useContext, useEffect, useMemo } from 'react';
import cx from 'classnames';

import { Dropdown } from 'components';
import { XS } from 'components/typography';
import { useChargeCodesOptions } from 'pages/portal/admin/channel/charges-invoices/hooks/useChargeCodesOptions';
import { useTranslation } from 'react-i18next';
import ChannelAdminContext from '../../ChannelAdminContext';
import { useBillingPaymentSettingsForChannel } from 'lane-shared/domains/billingPayments/hooks';
import { convertTo62 } from 'uuid-encoding';

import styles from './CreateProductForm.scss';

type MarkupChargeCodeDropdownProps = {
  markupChargeCodeId?: string;
  updateDetails: (value: any, field: string) => void;
};

export function MarkupChargeCodeDropdown({
  markupChargeCodeId,
  updateDetails,
}: MarkupChargeCodeDropdownProps) {
  const { t } = useTranslation();

  const { channel } = useContext(ChannelAdminContext);
  const channelId = channel?._id;

  const { markupChargeCodeId: rawDefaultMarkupChargeCodeId } =
    useBillingPaymentSettingsForChannel({ channelId });

  const defaultMarkupChargeCodeId = useMemo(
    () =>
      rawDefaultMarkupChargeCodeId
        ? convertTo62(rawDefaultMarkupChargeCodeId)
        : null,
    [rawDefaultMarkupChargeCodeId]
  );

  const chargeCodeOptions = useChargeCodesOptions(channelId);

  useEffect(() => {
    if (defaultMarkupChargeCodeId && !markupChargeCodeId) {
      updateDetails(defaultMarkupChargeCodeId, 'markupChargeCodeId');
    }
  }, [defaultMarkupChargeCodeId, markupChargeCodeId, updateDetails]);

  if (!defaultMarkupChargeCodeId) {
    return null;
  }

  const selectedMarkupChargeCodeId =
    markupChargeCodeId ?? defaultMarkupChargeCodeId;

  return (
    <div className="mb-2">
      <div className={cx(styles.label, 'mt-2')}>
        <label className={styles.descriptionLabel}>
          {t('abp.productsServices.createProduct.markupChargeCodeLabel')}
        </label>
      </div>
      <Dropdown
        testId="markupChargeCodeDropdown"
        doTranslation={false}
        className={cx(
          styles.inputFields,
          styles.inputWithCustomLabel,
          styles.categoryDropdown,
          'mb-0'
        )}
        value={selectedMarkupChargeCodeId}
        placeholder={t(
          'abp.productsServices.createProduct.chargeCode.placeholder'
        )}
        items={chargeCodeOptions}
        onChange={(item: { value: string }) => {
          updateDetails(item.value, 'markupChargeCodeId');
        }}
      />
      <XS variant="secondary">
        {t('abp.productsServices.createProduct.markupChargeCode.helperText')}
      </XS>
    </div>
  );
}
